// Fleet's local inference (components/FleetLocal.jsx), from its read
// (lib/fleetConsoleReads.js readFleetLocal): the models endpoints ran on their
// own and the endpoints that ran them, each split into the other, the
// runtimes present on each endpoint, and the runtimes installed and never seen
// running. Sessions are the only measure. A runtime is only ever present: a
// session never names the runtime that served it, and an endpoint can run
// several, so no session is split by runtime.
import { installedKey, modelKey, runtimeOf } from "./fleetConsole.js";
import { ownerOf } from "./fleetCoverage.js";
import { installedBy, runtimesIn, runtimesNotUsed } from "./fleetBreakdown.js";

const UNNAMED = "Unnamed endpoint";
const bySessions = (a, b) => b.sessions - a.sessions || a.name.localeCompare(b.name);
const sum = (rows) => rows.reduce((n, r) => n + (Number(r.sessions) || 0), 0);
const runtimeName = (rt) => (rt.key === "local" ? "Unnamed runtime" : rt.name);

/** Words for a list: "a", "a and b", "a, b and c". */
export function listed(words) {
  return words.length < 2 ? words[0] || "" : `${words.slice(0, -1).join(", ")} and ${words.at(-1)}`;
}

function namesOf(d) {
  const by = new Map();
  for (const r of d.names || []) by.set(String(r.id), { hostname: String(r.hostname || ""), owner: ownerOf(r.owner) });
  return by;
}

// One subject's rows from sessions [{ model, endpoint, sessions }], each with
// what it used, the most sessions first. `of` names a row and a part.
function group(rows, of) {
  const by = new Map();
  for (const r of rows || []) {
    const n = Number(r.sessions) || 0;
    const [id, name, partId, partName] = of(r);
    if (!id || !n) continue;
    if (!by.has(id)) by.set(id, { id, name, sessions: 0, used: new Map() });
    const row = by.get(id);
    row.sessions += n;
    const part = row.used.get(partId) || { id: partId, name: partName, sessions: 0 };
    part.sessions += n;
    row.used.set(partId, part);
  }
  return [...by.values()].map((row) => ({ ...row, used: [...row.used.values()].sort(bySessions) })).sort(bySessions);
}

/** Each subject's rows for a block's sessions; the names say whose endpoints. */
export function subjectsOf(rows, names = new Map()) {
  const host = (id) => names.get(id)?.hostname || UNNAMED;
  // A model's endpoints are named by whose they are, so its spread reads as people.
  const whose = (id) => {
    const n = names.get(id);
    return n?.owner && n?.hostname ? `${n.owner}, ${n.hostname}` : n?.owner || n?.hostname || UNNAMED;
  };
  return {
    models: group(rows, (r) => [modelKey(r.model), String(r.model), String(r.endpoint || ""), whose(String(r.endpoint || ""))]),
    endpoints: group(rows, (r) => [String(r.endpoint || ""), host(String(r.endpoint || "")), modelKey(r.model), String(r.model)]),
  };
}

/** A subject's rows in the block before, as each row's sessions and its parts' (lib/fleetList.js beforeOf). */
export function beforeOfRows(rows) {
  return rows ? new Map(rows.map((r) => [r.id, { sessions: r.sessions, used: new Map(r.used.map((u) => [u.id, u.sessions])) }])) : null;
}

/** The runtimes on each endpoint: those seen running, and those installed and not seen running there. */
export function runtimesByEndpoint(ran = [], installed = []) {
  const by = new Map();
  const at = (id) => {
    if (!by.has(id)) by.set(id, { ran: new Map(), installed: new Map() });
    return by.get(id);
  };
  for (const e of ran || []) {
    const rt = runtimeOf(e);
    const id = String(e.endpoint || "");
    if (rt && id) at(id).ran.set(rt.key, runtimeName(rt));
  }
  for (const i of installed || []) {
    const id = String(i.endpoint || "");
    if (id && i.name) at(id).installed.set(installedKey(i.name), String(i.name));
  }
  const out = new Map();
  for (const [id, r] of by) {
    out.set(id, { ran: [...r.ran.values()].sort(), installed: [...r.installed].filter(([k]) => !r.ran.has(k)).map(([, n]) => n).sort() });
  }
  return out;
}

/** What an endpoint row says of its runtimes: "Ollama and LM Studio ran, llama.cpp installed". */
export function runtimeWords(rt) {
  if (!rt) return "";
  return [rt.ran.length ? `${listed(rt.ran)} ran` : "", rt.installed.length ? `${listed(rt.installed)} installed` : ""].filter(Boolean).join(", ");
}

/**
 * The page: both subjects' rows (endpoints with a runtime but no local session
 * are rows too), each subject in the block before, the runtimes installed and
 * never seen running in the block, and the totals now and before.
 */
export function localOf(d) {
  const names = namesOf(d);
  const now = subjectsOf(d.now, names);
  const then = d.then ? subjectsOf(d.then, names) : null;
  const present = runtimesByEndpoint(d.ran, d.installed);
  const listedIds = new Set(now.endpoints.map((r) => r.id));
  const idle = [...present.keys()].filter((id) => !listedIds.has(id)).map((id) => ({ id, name: names.get(id)?.hostname || UNNAMED, sessions: 0, used: [] }));
  const endpoints = [...now.endpoints, ...idle].map((r) => ({ ...r, owner: names.get(r.id)?.owner || "", runtimes: present.get(r.id) || null }));
  // Runtimes installed somewhere and seen running nowhere in the block.
  const products = new Map();
  for (const i of d.installed || []) {
    if (!i.name || !i.endpoint) continue;
    if (!products.has(i.name)) products.set(i.name, new Set());
    products.get(i.name).add(String(i.endpoint));
  }
  const ranNow = d.ran ? runtimesIn(d.ran, []) : [];
  const unused = d.installed && d.ran
    ? runtimesNotUsed(installedBy([...products].map(([name, on]) => ({ name, installed: on.size }))), ranNow)
      .map((i) => ({ id: installedKey(i.name), name: i.name, unused: true, installed: i.installed, sessions: 0, used: [] }))
    : [];
  const running = (rows, ran) => new Set([...rows.map((r) => String(r.endpoint || "")), ...(ran || []).map((e) => String(e.endpoint || ""))].filter(Boolean)).size;
  return {
    models: now.models,
    endpoints,
    unused,
    before: { models: beforeOfRows(then?.models), endpoints: beforeOfRows(then?.endpoints) },
    totals: {
      now: { sessions: sum(d.now), models: now.models.length, endpoints: running(d.now, d.ran), unused: d.installed && d.ran ? unused.length : null },
      then: d.then ? { sessions: sum(d.then), models: then.models.length, endpoints: d.ranThen || !d.ran ? running(d.then, d.ranThen) : null } : null,
    },
  };
}
